import { useState } from 'react'
import type { MissionTeam, Participant, TacticalReport } from '../api/types'
import { CloseIcon } from './FeatureEditor'
import { Modal } from './Modal'
import { REPORT_NAMES, isOpen, reportPosition } from '../import/reports'

/**
 * One report: what was raised, by whom, and moving it along.
 *
 * Anybody who can see a request can take it -- the server's rule, since restricting it to medics
 * would leave requests unanswered -- so the status buttons are for everyone. Withdrawing ends it
 * and keeps it in the log; deleting it outright is a host's call, for duplicates and tests.
 */
export function ReportPanel({
  report,
  teams,
  roster,
  isHost,
  onStatus,
  onWithdraw,
  onDelete,
  onShowOnMap,
  onClose,
}: {
  report: TacticalReport
  teams: MissionTeam[]
  roster: Participant[]
  isHost: boolean
  onStatus: (status: TacticalReport['status']) => Promise<void>
  onWithdraw: () => Promise<void>
  onDelete: () => Promise<void>
  onShowOnMap?: () => void
  onClose: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState<'withdraw' | 'delete' | null>(null)
  const team = teams.find((entry) => entry.teamId === report.teamId)
  const raiser = roster.find((entry) => entry.userId === report.raisedBy)
  const open = isOpen(report)
  const position = reportPosition(report)

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    await action()
    setBusy(false)
  }

  const next: { status: TacticalReport['status']; label: string } | null =
    report.status === 'SUBMITTED'
      ? { status: 'ACKNOWLEDGED', label: 'Acknowledge' }
      : report.status === 'ACKNOWLEDGED'
        ? { status: 'IN_PROGRESS', label: 'Mark in progress' }
        : report.status === 'IN_PROGRESS'
          ? { status: 'COMPLETE', label: 'Mark complete' }
          : null

  return (
    <div className="floating-panel" role="dialog" aria-label={`Report ${report.title ?? ''}`}>
      <div className="spread" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="eyebrow">{REPORT_NAMES[report.type]}</div>
          <h2 style={{ marginTop: 2 }}>{report.title ?? REPORT_NAMES[report.type]}</h2>
        </div>
        <button className="icon-button" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </button>
      </div>

      <div className="row" style={{ gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <span className={`pill ${open ? 'danger' : 'neutral'}`}>
          {report.status.toLowerCase().replace('_', ' ')}
        </span>
        {report.highestPrecedence && (
          <span className="pill warning">{report.highestPrecedence.toLowerCase()}</span>
        )}
        {report.totalCasualties > 0 && (
          <span className="pill neutral">
            {report.totalCasualties} casualt{report.totalCasualties === 1 ? 'y' : 'ies'}
          </span>
        )}
      </div>

      <p className="tertiary" style={{ margin: '10px 0 0' }}>
        {[raiser?.callsign, team?.teamName].filter(Boolean).join(' · ') || 'Unknown sender'}
        {report.createdAt &&
          ` · ${new Date(report.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
      </p>
      {position && (
        <p className="mono tertiary" style={{ margin: '4px 0 0' }}>
          {position[0].toFixed(5)}, {position[1].toFixed(5)}
        </p>
      )}

      {report.remarks && <p style={{ whiteSpace: 'pre-wrap' }}>{report.remarks}</p>}

      <div className="stack" style={{ marginTop: 14, gap: 8 }}>
        {next && (
          <button disabled={busy} onClick={() => void run(() => onStatus(next.status))}>
            {next.label}
          </button>
        )}
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {position && onShowOnMap && (
            <button className="secondary" onClick={onShowOnMap}>
              Show on map
            </button>
          )}
          {open && (
            <button className="secondary" disabled={busy} onClick={() => setConfirming('withdraw')}>
              Withdraw
            </button>
          )}
          {isHost && (
            <button className="danger-outline" disabled={busy} onClick={() => setConfirming('delete')}>
              Delete
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <Modal
          title={confirming === 'delete' ? 'Delete this report?' : 'Withdraw this report?'}
          onClose={() => setConfirming(null)}
        >
          <div className="stack">
            <p style={{ margin: 0 }}>
              {confirming === 'delete'
                ? 'It is removed from the record and from every phone, with its marker on the map. Use this for duplicates and tests; to close a real request, withdraw or complete it instead.'
                : 'It is closed as no longer needed, and stays in the event log.'}
            </p>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <button className="secondary" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                className="danger"
                disabled={busy}
                onClick={async () => {
                  await run(confirming === 'delete' ? onDelete : onWithdraw)
                  setConfirming(null)
                }}
              >
                {confirming === 'delete' ? 'Delete' : 'Withdraw'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
